import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getRoot, $getSelection, EditorState, RangeSelection } from 'lexical';
import React, { useEffect } from 'react';
import { ListNode } from '@lexical/list';
import { useEmailContext } from '../../../../../../contexts/EmailContext';

interface EmailEditorEventPluginProps {
  onChange: (editorState: EditorState) => void;
  setFormat: React.Dispatch<React.SetStateAction<[boolean, boolean]>>;
  setActiveListType: React.Dispatch<React.SetStateAction<'ordered' | 'unordered' | null>>;
}

export function EmailEditorEventPlugin({ onChange, setFormat, setActiveListType }: EmailEditorEventPluginProps): null {
  const [editor] = useLexicalComposerContext();
  const { selectedEmail, getEmailDraftBodyState, updateEmailDraft } = useEmailContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      onChange(editorState);
      editorState.read(() => {
        const selection = $getSelection() as RangeSelection;

        const root = $getRoot();
        const children = root.getChildren();
        const lastNode = children.at(-1);

        if (selection?.hasFormat('bold') || selection?.hasFormat('italic')) {
          setFormat([selection.hasFormat('bold'), selection.hasFormat('italic')]);
        } else {
          setFormat([false, false]);
        }

        if (!(lastNode instanceof ListNode)) {
          setActiveListType(null);
        }

        if (selectedEmail) {
          updateEmailDraft(selectedEmail, { bodyState: editor.getEditorState() });
        }
      });
    });
  }, [editor, onChange, selectedEmail, setActiveListType, setFormat, updateEmailDraft]);

  useEffect(() => {
    const savedDraftBodyState = selectedEmail ? getEmailDraftBodyState(selectedEmail) : undefined;

    if (savedDraftBodyState) {
      editor.setEditorState(savedDraftBodyState);
      return;
    }

    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      root.append(paragraph);
      paragraph.select();
    });
  }, [editor, getEmailDraftBodyState, selectedEmail]);

  return null;
}
